/**
 * Backtest (roadmap B2): ask when something happened. Laid out as the sketch
 * `docs/design/sketches/argus-quicktest-v2.html`:
 *
 * - **The ask bar**, on top: the Question (a custom question in blocks, or a
 *   rule's blocks), the Window (Explore's presets, More, two dates, and All
 *   time, held), and **Find episodes**.
 * - **The question**, left: the rule editor's blocks in `quick` mode -
 *   read-only when a rule is picked, until **Edit as custom question**.
 * - **The answer**, right: `argus-backtest-result`, run by the watcher with
 *   the code that raises real alerts. An edited question dims it; it never
 *   runs again by itself.
 * - **Your questions** and **Rule backtests**, below, folded: lists in the
 *   Rules room's table style.
 *
 * A question is never a rule (migration 0045): not saved as one, never
 * deployed, never an alert, never counted for deploying - a picked rule's
 * blocks included. **Save as draft rule** hands the blocks to the Rules room's
 * editor, unsaved. Nothing here evaluates (rule 3).
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { canDo } from '../../state/store.ts';
import { getArgusBacktest, listArgusBacktests, requestArgusBacktest } from '../../api/endpoints.ts';
import type { ArgusBacktest, ArgusBacktestSummary, ArgusRule, ArgusTreeDocument } from '../../api/types.ts';
import { userMessage } from '../../api/errors.ts';
import { backtestQueue, backtestSelected, queueWords, refreshBacktestQueue } from './argus-backtest-store.ts';
import './argus-backtest-result.ts';
import './argus-rule-editor.ts';
import './argus-window-pick.ts';
import type { QuickChange } from './argus-rule-editor.ts';
import {
  assistantDraft,
  inNoTeam,
  loadRules,
  loadTeams,
  loadSources,
  loadWatchers,
  rules,
  sources,
  teams,
  teamsLoaded,
  type AssistantRuleDraft,
} from './argus-rules-store.ts';
import { setArgusRoom } from './argus-lens.ts';
import { relativeTime } from './argus-rules-room.ts';
import {
  DEFAULT_WINDOW,
  windowDays,
  windowIsHeavy,
  windowKey,
  windowOfRun,
  windowProblem,
  windowRequest,
  windowTitle,
  windowWords,
  type BacktestWindow,
} from './argus-window-pick.ts';

const POLL_MS = 1500;
const FOLDS_KEY = 'argus-backtest-folds';

type Fold = 'mine' | 'rules';
type MineFilter = 'all' | 'done' | 'failed';
type RuleFilter = 'all' | 'deployed' | 'draft';

function severityOfRule(rule: ArgusRule | undefined): string {
  return ((rule?.tree?.document ?? rule?.document) as { severity?: string } | null | undefined)?.severity ?? 'watch';
}

function readFolds(): Record<Fold, boolean> {
  try {
    return { mine: false, rules: false, ...JSON.parse(localStorage.getItem(FOLDS_KEY) ?? '{}') };
  } catch {
    return { mine: false, rules: false };
  }
}

@customElement('argus-backtest-room')
export class ArgusBacktestRoom extends LightElement {
  private store = new StoreController(this, [backtestSelected, backtestQueue, sources, rules, teams]);

  @state() private runs: ArgusBacktestSummary[] | null = null;
  @state() private listError: string | null = null;
  @state() private run: ArgusBacktest | null = null;
  @state() private window: BacktestWindow = DEFAULT_WINDOW;
  /** The question on screen, as the editor last told it. */
  @state() private question: QuickChange | null = null;
  /** What the run on the right was asked with; differs from the screen once edited. */
  @state() private askedKey: string | null = null;
  @state() private asking = false;
  @state() private askError: string | null = null;
  /** The editor opens on this document; a new key opens a new editor. */
  @state() private seed: AssistantRuleDraft | null = null;
  @state() private editorKey = 'new-0';
  /** The rule whose blocks are on screen, read-only; null for a custom question. */
  @state() private ruleId: string | null = null;
  /** The version those blocks are: the rule's current one, or the one a run ran. */
  @state() private ruleVersion: number | null = null;
  @state() private pickerOpen = false;
  @state() private pickerQuery = '';
  @state() private folds = readFolds();
  @state() private mineQuery = '';
  @state() private mineFilter: MineFilter = 'all';
  @state() private rulesQuery = '';
  @state() private ruleFilter: RuleFilter = 'all';
  /** The editor opens once the sources are known: it reads its source once. */
  @state() private sourcesRead = sources.value.length > 0;
  /** The next change the editor tells is the opened run's own question. */
  private adoptNext = false;
  private loadedId: string | null = null;
  private timer: number | undefined;

  override connectedCallback(): void {
    super.connectedCallback();
    void loadWatchers();
    void this.loadRuns();
    void refreshBacktestQueue();
    if (!rules.value.length) void loadRules();
    if (!teamsLoaded.value) void loadTeams();
    if (!this.sourcesRead) void loadSources().finally(() => (this.sourcesRead = true));
    document.addEventListener('pointerdown', this.onOutside, true);
    document.addEventListener('keydown', this.onKey);
  }

  override disconnectedCallback(): void {
    document.removeEventListener('pointerdown', this.onOutside, true);
    document.removeEventListener('keydown', this.onKey);
    window.clearTimeout(this.timer);
    super.disconnectedCallback();
  }

  private onOutside = (e: Event): void => {
    if (this.pickerOpen && !(e.target as Element | null)?.closest('.argus-qt-picker')) this.pickerOpen = false;
  };

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.pickerOpen = false;
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      void this.ask();
    }
  };

  private async loadRuns(): Promise<void> {
    try {
      this.runs = (await listArgusBacktests()).backtests;
      this.listError = null;
    } catch (error) {
      this.listError = userMessage(error, 'The earlier runs could not be read.');
    }
  }

  override updated(): void {
    const id = backtestSelected.value;
    if (id !== this.loadedId) {
      this.loadedId = id;
      this.run = null;
      if (id) void this.follow(id, true);
    }
  }

  /** Read a run, and keep reading while the watcher is on it. */
  private async follow(id: string, open = false): Promise<void> {
    window.clearTimeout(this.timer);
    try {
      const run = await getArgusBacktest(id);
      if (id !== backtestSelected.value) return;
      this.run = run;
      if (open && run.document) this.openQuestion(run);
      if (run.status === 'queued' || run.status === 'running') {
        this.timer = window.setTimeout(() => void this.follow(id), POLL_MS);
      } else if (this.runs?.some((r) => r.id === id && r.status !== run.status)) {
        void this.loadRuns();
        void refreshBacktestQueue();
      }
    } catch {
      this.timer = window.setTimeout(() => void this.follow(id), POLL_MS * 2);
    }
  }

  /** Put a run's question and window back: a rule's backtest as that rule's blocks. */
  private openQuestion(run: ArgusBacktest): void {
    const rule = run.rule_id ? rules.value.find((r) => r.id === run.rule_id) : undefined;
    this.ruleId = rule ? rule.id : null;
    this.ruleVersion = rule ? run.rule_version : null;
    this.seed = { ruleId: null, name: '', document: run.document!, assumptions: [] };
    this.window = windowOfRun(run);
    this.editorKey = `run-${run.id}`;
    this.adoptNext = true;
    this.askedKey = null;
    this.askError = null;
  }

  /** The picker: a rule's current blocks, read-only, or a fresh custom question. */
  private pick(rule: ArgusRule | null): void {
    this.pickerOpen = false;
    this.askError = null;
    if (rule) {
      const document = rule.tree?.document;
      if (!document) return;
      this.ruleId = rule.id;
      this.ruleVersion = rule.version;
      this.seed = { ruleId: null, name: '', document, assumptions: [] };
      this.editorKey = `rule-${rule.id}-${Date.now()}`;
    } else {
      this.ruleId = null;
      this.ruleVersion = null;
      this.seed = null;
      this.editorKey = `new-${Date.now()}`;
    }
    this.question = null;
  }

  /** Keep the blocks on screen, now editable: the rule becomes a custom question. */
  private editAsCustom(): void {
    const q = this.question;
    this.ruleId = null;
    this.ruleVersion = null;
    if (q) {
      this.seed = { ruleId: null, name: '', document: q.document, assumptions: [] };
      this.editorKey = `custom-${Date.now()}`;
    }
  }

  private newQuestion(): void {
    this.pick(null);
    this.window = DEFAULT_WINDOW;
    this.askedKey = null;
    backtestSelected.set(null);
  }

  private keyOf(question: QuickChange | null, window: BacktestWindow): string | null {
    return question ? `${JSON.stringify(question.document)}|${windowKey(window)}` : null;
  }

  private onQuestion(change: QuickChange): void {
    this.question = change;
    if (this.adoptNext) {
      this.adoptNext = false;
      this.askedKey = this.keyOf(change, this.window);
    }
  }

  private busy(): boolean {
    return this.asking || this.run?.status === 'queued' || this.run?.status === 'running';
  }

  private async ask(): Promise<void> {
    const q = this.question;
    // A run already waiting does not stop another: the watchers queue them.
    if (!q?.ok || windowProblem(this.window) || this.asking) return;
    this.asking = true;
    this.askError = null;
    const rule = this.ruleId ? rules.value.find((r) => r.id === this.ruleId) : undefined;
    try {
      const run = await requestArgusBacktest({
        document: q.document,
        purpose: 'quick',
        label: (rule?.name ?? q.sentence ?? '').slice(0, 200),
        ...windowRequest(this.window),
      });
      this.askedKey = this.keyOf(q, this.window);
      this.loadedId = run.id; // this run is on screen already; do not reopen its question
      backtestSelected.set(run.id);
      this.run = run;
      void this.loadRuns();
      void this.follow(run.id);
      void refreshBacktestQueue();
    } catch (error) {
      this.askError = userMessage(error, 'The question could not be asked.');
    } finally {
      this.asking = false;
    }
  }

  /** Hand the question to the Rules room's editor, unsaved. The person saves it there. */
  private saveAsRule(): void {
    const q = this.question;
    if (!q?.ok) return;
    assistantDraft.set({ ruleId: null, name: '', document: q.document as ArgusTreeDocument, assumptions: [] });
    setArgusRoom('rules');
  }

  private setFold(which: Fold): void {
    this.folds = { ...this.folds, [which]: !this.folds[which] };
    try {
      localStorage.setItem(FOLDS_KEY, JSON.stringify(this.folds));
    } catch {
      /* a remembered fold is a convenience */
    }
  }

  /* ── the ask bar ─────────────────────────────────────────── */

  private renderPicker(): TemplateResult {
    const rule = this.ruleId ? rules.value.find((r) => r.id === this.ruleId) : undefined;
    const query = this.pickerQuery.trim().toLowerCase();
    const listed = rules.value
      .filter((r) => r.status !== 'retired' && r.tree?.document)
      .filter((r) => !query || r.name.toLowerCase().includes(query) || (r.sentence ?? '').toLowerCase().includes(query));
    return html`<div class="argus-qt-picker">
      <button
        type="button"
        class="argus-qt-pick-btn"
        aria-haspopup="menu"
        aria-expanded=${this.pickerOpen ? 'true' : 'false'}
        @click=${() => {
          this.pickerOpen = !this.pickerOpen;
          this.pickerQuery = '';
        }}
      >
        ${rule
          ? html`<span class="argus-qt-sev is-${severityOfRule(rule)}" aria-hidden="true"></span>
              <span class="argus-qt-pick-name">${rule.name}</span>
              <span class="argus-chip is-${rule.status}">${rule.status}</span>`
          : html`<span class="argus-qt-sev" aria-hidden="true"></span>
              <span class="argus-qt-pick-name">Custom question</span>
              <span class="argus-lfaint">blocks</span>`}
        <span class="preset-more-caret" aria-hidden="true"></span>
      </button>
      ${this.pickerOpen
        ? html`<div class="cat-menu argus-qt-menu" role="menu">
            <input
              type="search"
              class="argus-search"
              placeholder="Find a rule…"
              aria-label="Find a rule"
              .value=${this.pickerQuery}
              @input=${(e: Event) => (this.pickerQuery = (e.target as HTMLInputElement).value)}
            />
            <div class="argus-qt-menu-scroll">
              <button type="button" class="cat-menu-item ${this.ruleId ? '' : 'is-active'}" role="menuitemradio" aria-checked=${this.ruleId ? 'false' : 'true'} @click=${() => this.pick(null)}>
                <span class="argus-qt-tick">${this.ruleId ? '' : '✓'}</span>
                <span class="argus-qt-item">Custom question<small>Build your own with the rule blocks</small></span>
              </button>
              <div class="argus-qt-menu-sep"></div>
              <span class="argus-toolbar-label argus-qt-menu-label">Rules · their current version</span>
              ${listed.length
                ? listed.map(
                    (r) => html`<button
                      type="button"
                      class="cat-menu-item ${r.id === this.ruleId ? 'is-active' : ''}"
                      role="menuitemradio"
                      aria-checked=${r.id === this.ruleId ? 'true' : 'false'}
                      @click=${() => this.pick(r)}
                    >
                      <span class="argus-qt-tick">${r.id === this.ruleId ? '✓' : ''}</span>
                      <span class="argus-qt-sev is-${severityOfRule(r)}" aria-hidden="true"></span>
                      <span class="argus-qt-item">${r.name}<small>v${r.version} · ${r.owner_name}</small></span>
                      <span class="argus-chip is-${r.status}">${r.status}</span>
                    </button>`,
                  )
                : html`<p class="argus-lfaint argus-qt-menu-empty">${rules.value.length ? 'No rule matches.' : 'No rules yet.'}</p>`}
            </div>
          </div>`
        : nothing}
    </div>`;
  }

  private renderBar(): TemplateResult {
    const q = this.question;
    const problem = windowProblem(this.window);
    const queue = queueWords(backtestQueue.value);
    const days = windowDays(this.window);
    const heavy = windowIsHeavy(this.window);
    const reach = q?.entities != null ? `${q.entities} ${q.noun}${q.entities === 1 ? '' : 's'}` : null;
    const estimate = [reach, days != null ? `${days} days` : 'all time', heavy ? 'heavy' : null].filter(Boolean).join(' · ');
    const why = problem ? null : q && !q.ok ? (q.error ? 'The question is not whole yet - see Reads as.' : 'Reading the question…') : null;
    return html`<section class="argus-qt-bar" aria-label="Ask">
      <div class="argus-qt-group">
        <span class="argus-toolbar-label">Question</span>
        ${this.renderPicker()}
      </div>
      <div class="argus-qt-group">
        <span class="argus-toolbar-label">Window</span>
        <argus-window-pick
          .value=${this.window}
          .entities=${q?.entities ?? null}
          .noun=${q?.noun ?? 'ship'}
          @window-change=${(e: CustomEvent<BacktestWindow>) => (this.window = e.detail)}
        ></argus-window-pick>
      </div>
      <div class="argus-qt-go">
        <span class="argus-lfaint ${heavy ? 'argus-qt-heavy' : ''}" title=${windowTitle(this.window)}>${estimate}</span>
        <button
          type="button"
          class="btn btn-primary"
          ?disabled=${this.asking || !q?.ok || !!problem}
          title=${why ?? 'Ctrl+Enter'}
          @pointerenter=${() => void refreshBacktestQueue()}
          @focus=${() => void refreshBacktestQueue()}
          @click=${() => void this.ask()}
        >
          ${this.asking ? 'Finding…' : 'Find episodes'}
        </button>
      </div>
      ${queue ? html`<p class="argus-lfaint argus-qt-queue" role="status">${queue}</p>` : nothing}
      ${this.askError ? html`<p class="argus-form-error argus-qt-bar-error" role="alert">${this.askError}</p>` : nothing}
    </section>`;
  }

  /* ── the question and the answer ─────────────────────────── */

  private renderAsk(source: string): TemplateResult {
    const rule = this.ruleId ? rules.value.find((r) => r.id === this.ruleId) : undefined;
    return html`<aside class="argus-qt-ask" aria-label="The question">
      <div class="argus-qt-ask-head">
        <h4>${rule ? 'The rule, as a question' : 'Your question'}</h4>
        <button type="button" class="argus-lmore" @click=${() => this.newQuestion()}>New question</button>
      </div>
      ${rule
        ? html`<div class="argus-qt-from-rule">
            <span>
              The blocks of <b>${rule.name}</b>, version ${this.ruleVersion ?? rule.version}. Run here as a
              question: it does not count for deploying the rule.
            </span>
            <button type="button" class="dash-btn" @click=${() => this.editAsCustom()}>Edit as custom question</button>
          </div>`
        : nothing}
      ${!this.sourcesRead
        ? html`<p class="argus-lfaint">Opening the editor…</p>`
        : keyed(
            this.editorKey,
            html`<argus-rule-editor
              quick
              ?readonly=${!!rule}
              .draft=${this.seed}
              .sourceKey=${source}
              @argus-quick-change=${(e: CustomEvent<QuickChange>) => this.onQuestion(e.detail)}
            ></argus-rule-editor>`,
          )}
    </aside>`;
  }

  private renderAnswer(): TemplateResult {
    const run = this.run;
    const current = this.keyOf(this.question, this.window);
    const stale = !!run && this.askedKey !== null && current !== null && current !== this.askedKey && !this.busy();
    if (!backtestSelected.value) {
      return html`<div class="argus-qt-empty">
        <h3>Ask when something happened</h3>
        <p>
          Pick a rule or build a question, choose a window, and press <b>Find episodes</b>. The watcher
          runs it over past data with the code that raises real alerts.
        </p>
        <p class="argus-lfaint">
          Explore shows the readings that match a filter. Backtest joins them into episodes: when each
          began, how long it lasted, and on which ship.
        </p>
      </div>`;
    }
    if (!run) return html`<p class="argus-lfaint">Reading the run…</p>`;
    return html`
      ${stale
        ? html`<div class="argus-bt-stale" role="status">
            <span>The question has changed. This answer is for the one asked before.</span>
            <button type="button" class="dash-btn" ?disabled=${!this.question?.ok || this.asking} @click=${() => void this.ask()}>
              Find episodes again
            </button>
          </div>`
        : nothing}
      <div class="argus-qt-result ${stale ? 'is-stale' : ''}">
        <argus-backtest-result .run=${run}></argus-backtest-result>
      </div>
      ${canDo('argus:author') && !inNoTeam() && this.question?.ok && run.status === 'done'
        ? html`<div class="argus-qt-actions">
            <button type="button" class="dash-btn" @click=${() => this.saveAsRule()}>Save as draft rule</button>
            <span class="argus-lfaint">Opens the rule editor with these blocks. It is saved, and runs, only when you say so.</span>
          </div>`
        : nothing}
    `;
  }

  /* ── the two lists ───────────────────────────────────────── */

  private found(r: ArgusBacktestSummary): TemplateResult {
    if (r.status === 'queued' || r.status === 'running') return html`<span class="argus-lfaint">${r.status === 'queued' ? 'waiting…' : 'running…'}</span>`;
    if (r.status === 'failed') {
      return html`<div class="argus-qt-failed">could not run</div>
        <div class="argus-lfaint argus-ltext" title=${r.error ?? ''}>${r.error ?? ''}</div>`;
    }
    const t = r.totals;
    return t
      ? html`<div><b>${t.episodes.toLocaleString()}</b> episode${t.episodes === 1 ? '' : 's'}</div>
          <div class="argus-lfaint">${t.entities_firing} of ${t.entities}</div>`
      : html`-`;
  }

  private windowCell(r: ArgusBacktestSummary): TemplateResult {
    return html`<div class="argus-ltext" title=${windowWords(r)}>${windowWords(r)}</div>
      <div class="argus-lfaint">${r.all_time ? 'from where the data begins' : r.range_from ? 'UTC, whole days' : 'ending when run'}</div>`;
  }

  private foldHead(which: Fold, title: string, count: number, about: string, latest: string): TemplateResult {
    const open = this.folds[which];
    return html`<button
      type="button"
      class="argus-qt-fold-h"
      aria-expanded=${open ? 'true' : 'false'}
      aria-controls=${`argus-qt-${which}`}
      @click=${() => this.setFold(which)}
    >
      <span class="argus-qt-fold-caret" aria-hidden="true"></span>
      <h2 class="argus-qt-fold-title">${title} <span class="argus-qt-count">${count}</span></h2>
      <span class="argus-lfaint">${about}</span>
      <span class="argus-qt-fold-sum">${latest}</span>
    </button>`;
  }

  private chip<T extends string>(on: T, value: T, label: string, n: number, set: (v: T) => void): TemplateResult {
    return html`<button type="button" class="argus-fchip ${on === value ? 'is-on' : ''}" @click=${() => set(value)}>
      ${label} <b>${n}</b>
    </button>`;
  }

  private renderMine(all: ArgusBacktestSummary[]): TemplateResult {
    const q = this.mineQuery.trim().toLowerCase();
    const shown = all
      .filter((r) => this.mineFilter === 'all' || r.status === this.mineFilter)
      .filter((r) => !q || r.label.toLowerCase().includes(q));
    const latest = all[0] ? `latest: ${all[0].label || 'Untitled question'} · ${relativeTime(all[0].requested_at)}` : 'none yet';
    return html`<section class="argus-qt-list is-mine" aria-label="Your questions">
      ${this.foldHead('mine', 'Your questions', all.length, 'only you see these. Not rules, never deployed, never an alert.', latest)}
      ${this.folds.mine
        ? html`<div class="argus-qt-fold-b" id="argus-qt-mine">
            <div class="argus-toolbar">
              <input class="argus-search" type="search" placeholder="Find a question, a signal…" aria-label="Find in your questions" .value=${this.mineQuery} @input=${(e: Event) => (this.mineQuery = (e.target as HTMLInputElement).value)} />
              ${this.chip(this.mineFilter, 'all', 'All', all.length, (v) => (this.mineFilter = v))}
              ${this.chip(this.mineFilter, 'done', 'Finished', all.filter((r) => r.status === 'done').length, (v) => (this.mineFilter = v))}
              ${this.chip(this.mineFilter, 'failed', 'Could not run', all.filter((r) => r.status === 'failed').length, (v) => (this.mineFilter = v))}
            </div>
            <div class="argus-ltable-wrap">
              <table class="argus-ltable">
                <colgroup><col /><col class="argus-qt-col-window" /><col class="argus-qt-col-found" /><col class="argus-lcol-when" /></colgroup>
                <thead><tr><th>Question</th><th>Window</th><th>Found</th><th>Asked</th></tr></thead>
                <tbody>
                  ${shown.length
                    ? shown.map(
                        (r) => html`<tr
                          class="argus-lrow is-quick ${r.id === backtestSelected.value ? 'is-selected' : ''}"
                          tabindex="0"
                          @click=${() => this.openRow(r)}
                          @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this.openRow(r)}
                        >
                          <td><div class="argus-lname"><span class="argus-ltext" title=${r.label}>${r.label || 'Untitled question'}</span></div></td>
                          <td>${this.windowCell(r)}</td>
                          <td>${this.found(r)}</td>
                          <td class="argus-lfaint"><div class="argus-ltext">${relativeTime(r.requested_at)}</div></td>
                        </tr>`,
                      )
                    : html`<tr><td colspan="4" class="argus-lempty">${all.length ? 'No question matches.' : 'None yet. Your questions are listed here, and only to you.'}</td></tr>`}
                </tbody>
              </table>
            </div>
          </div>`
        : nothing}
    </section>`;
  }

  private renderRuleRuns(all: ArgusBacktestSummary[]): TemplateResult {
    const byId = new Map(rules.value.map((r) => [r.id, r]));
    const q = this.rulesQuery.trim().toLowerCase();
    const status = (r: ArgusBacktestSummary): string | undefined => (r.rule_id ? byId.get(r.rule_id)?.status : undefined);
    const shown = all
      .filter((r) => this.ruleFilter === 'all' || status(r) === this.ruleFilter)
      .filter((r) => !q || r.label.toLowerCase().includes(q) || (r.requested_by_name ?? '').toLowerCase().includes(q));
    const latest = all[0] ? `latest: ${all[0].label} · ${relativeTime(all[0].requested_at)}` : 'none yet';
    return html`<section class="argus-qt-list" aria-label="Rule backtests">
      ${this.foldHead('rules', 'Rule backtests', all.length, "every rule's backtest, anyone's. A finished one of the current version lets the rule be deployed.", latest)}
      ${this.folds.rules
        ? html`<div class="argus-qt-fold-b" id="argus-qt-rules">
            <div class="argus-toolbar">
              <input class="argus-search" type="search" placeholder="Find a rule, a person…" aria-label="Find in rule backtests" .value=${this.rulesQuery} @input=${(e: Event) => (this.rulesQuery = (e.target as HTMLInputElement).value)} />
              ${this.chip(this.ruleFilter, 'all', 'All', all.length, (v) => (this.ruleFilter = v))}
              ${this.chip(this.ruleFilter, 'deployed', 'Deployed', all.filter((r) => status(r) === 'deployed').length, (v) => (this.ruleFilter = v))}
              ${this.chip(this.ruleFilter, 'draft', 'Drafts', all.filter((r) => status(r) === 'draft').length, (v) => (this.ruleFilter = v))}
            </div>
            <div class="argus-ltable-wrap">
              <table class="argus-ltable">
                <colgroup>
                  <col /><col class="argus-qt-col-version" /><col class="argus-qt-col-window" /><col class="argus-qt-col-found" />
                  <col class="argus-lcol-sev-word" /><col class="argus-lcol-owner" /><col class="argus-lcol-when" />
                </colgroup>
                <thead><tr><th>Rule</th><th>Version</th><th>Window</th><th>Found</th><th>Severity</th><th>Asked by</th><th>When</th></tr></thead>
                <tbody>
                  ${shown.length
                    ? shown.map((r) => {
                        const rule = r.rule_id ? byId.get(r.rule_id) : undefined;
                        const sev = severityOfRule(rule);
                        const counts = !!rule && r.status === 'done' && r.rule_version === rule.version;
                        return html`<tr
                          class="argus-lrow is-sev-${sev} ${r.id === backtestSelected.value ? 'is-selected' : ''}"
                          tabindex="0"
                          @click=${() => this.openRow(r)}
                          @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this.openRow(r)}
                        >
                          <td>
                            <div class="argus-lname">
                              <span class="argus-ltext">${rule?.name ?? r.label}</span>
                              ${rule && rule.status !== 'deployed' ? html`<span class="argus-chip is-${rule.status}">${rule.status}</span>` : nothing}
                              ${!rule && r.rule_id === null ? html`<span class="argus-chip is-draft">unsaved draft</span>` : nothing}
                            </div>
                            <div class="argus-lsentence argus-ltext" title=${rule?.sentence ?? ''}>${rule?.sentence ?? ''}</div>
                          </td>
                          <td>
                            ${r.rule_version ? `v${r.rule_version}` : '-'}
                            ${counts ? html`<div class="argus-lfaint argus-qt-counts" title="A finished backtest of the current version: the rule may be deployed">counts</div>` : nothing}
                          </td>
                          <td>${this.windowCell(r)}</td>
                          <td>${this.found(r)}</td>
                          <td>${rule ? html`<span class="argus-chip is-${sev}">${sev}</span>` : '-'}</td>
                          <td><div class="argus-ltext" title=${r.requested_by_name}>${r.requested_by_name}</div></td>
                          <td class="argus-lfaint"><div class="argus-ltext">${relativeTime(r.requested_at)}</div></td>
                        </tr>`;
                      })
                    : html`<tr><td colspan="7" class="argus-lempty">${all.length ? 'No backtest matches.' : "None yet. A rule's drawer and the rule editor run them."}</td></tr>`}
                </tbody>
              </table>
            </div>
          </div>`
        : nothing}
    </section>`;
  }

  private openRow(r: ArgusBacktestSummary): void {
    backtestSelected.set(r.id);
    this.closest('.argus')?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private renderLists(): TemplateResult {
    if (this.listError) return html`<p class="argus-form-error">${this.listError}</p>`;
    if (!this.runs) return html`<p class="argus-lfaint">Reading earlier runs…</p>`;
    return html`${this.renderMine(this.runs.filter((r) => r.purpose === 'quick'))}
    ${this.renderRuleRuns(this.runs.filter((r) => r.purpose === 'rule'))}`;
  }

  override render(): TemplateResult {
    const source = sources.value[0]?.key ?? 'historical';
    return html`<div class="argus-qt-page">
      <div class="argus-ibx-head">
        <h2 class="argus-ibx-title">Backtest</h2>
        <span class="argus-lfaint">ask when something happened, on past data. Nothing is saved as a rule, and nobody gets an alert.</span>
      </div>
      ${this.renderBar()}
      <div class="argus-qt">
        ${this.renderAsk(source)}
        <div class="argus-qt-answer argus-btr-main">${this.renderAnswer()}</div>
      </div>
      ${this.renderLists()}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'argus-backtest-room': ArgusBacktestRoom;
  }
}
